/**
 * i18n (docs/09 "Accessibility and i18n"): every UI string goes through `t('module.key')`.
 * Catalogues are discovered like nav: the shell's `app/i18n/en.json` plus every
 * `features/<module>/i18n/<locale>.json`, merged. Keys are flat and prefixed by module
 * (`community.events.empty`). Locale selection exists from day one, with one locale.
 */
import {
  createContext,
  createElement,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import shellEn from './i18n/en.json';

export type Locale = 'en';
export const LOCALES: readonly Locale[] = ['en'];
type Catalogue = Record<string, string>;

const featureCatalogues = import.meta.glob<Catalogue>('../features/*/i18n/*.json', {
  eager: true,
  import: 'default',
});

function catalogueFor(locale: Locale): Catalogue {
  const merged: Catalogue = { ...(shellEn as Catalogue) };
  for (const [path, cat] of Object.entries(featureCatalogues)) {
    if (path.endsWith(`/${locale}.json`)) Object.assign(merged, cat);
  }
  return merged;
}

const CATALOGUES: Record<Locale, Catalogue> = { en: catalogueFor('en') };

export type TFunction = (key: string, vars?: Record<string, string | number | null | undefined>) => string;

export function translate(
  locale: Locale,
  key: string,
  vars?: Record<string, string | number | null | undefined>,
): string {
  const template = CATALOGUES[locale][key] ?? CATALOGUES.en[key];
  if (template === undefined) {
    if (import.meta.env.DEV && import.meta.env.MODE !== 'test') console.warn(`i18n: missing key ${key}`);
    return key;
  }
  return vars ? template.replace(/\{(\w+)\}/g, (_, name: string) => String(vars[name] ?? '')) : template;
}

/** Non-React callers (nav labels resolved at render time use the hook). */
export const t: TFunction = (key, vars) => translate('en', key, vars);

type I18nApi = { locale: Locale; setLocale(l: Locale): void; t: TFunction };
const I18nContext = createContext<I18nApi>({ locale: 'en', setLocale: () => {}, t });

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocale] = useState<Locale>('en');
  const tt = useCallback<TFunction>((key, vars) => translate(locale, key, vars), [locale]);
  const value = useMemo(() => ({ locale, setLocale, t: tt }), [locale, tt]);
  return createElement(I18nContext.Provider, { value }, children);
}

export function useI18n(): I18nApi {
  return useContext(I18nContext);
}

export function useT(): TFunction {
  return useContext(I18nContext).t;
}
